/**
 * ModulesTab — gestión de módulos activados en la suite + branding.
 *
 * Permite al admin:
 *  - Elegir un preset de industria (ferretería, retail, restaurante, servicios)
 *  - Toggle individual por módulo (con validación de dependencias)
 *  - Personalizar el nombre del producto (ej. reseller rebrand)
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Palette, Package2, Store, UtensilsCrossed, Briefcase, Wrench, Search, X, ToggleRight, ToggleLeft } from "lucide-react";
import { useBrand } from "@/context/BrandContext";

const PRESET_META = {
  hardware:   { icon: Wrench,            label: "Ferretería",  color: "text-amber-600" },
  retail:     { icon: Store,             label: "Retail / Tienda", color: "text-blue-600" },
  restaurant: { icon: UtensilsCrossed,   label: "Restaurante", color: "text-rose-600" },
  services:   { icon: Briefcase,         label: "Servicios",   color: "text-emerald-600" },
};

const CATEGORY_LABELS = {
  core:         "Núcleo (siempre activo)",
  ventas:       "Ventas",
  catalogo:     "Catálogo",
  compras:      "Compras",
  finanzas:     "Finanzas",
  fiscal:       "Fiscal (DGII RD)",
  operaciones:  "Operaciones",
  rrhh:         "Recursos Humanos",
};

export default function ModulesTab() {
  const { refresh: refreshBrand } = useBrand();
  const [state, setState] = useState(null);
  const [brand, setBrand] = useState({ product_name: "", display_short: "", display_tagline: "", primary_color: "", logo_url: "" });
  const [saving, setSaving] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);

  const load = async () => {
    try {
      const [{ data: m }, { data: b }] = await Promise.all([
        api.get("/settings/modules"),
        api.get("/settings/branding"),
      ]);
      setState(m);
      setBrand({
        product_name: b.product_name || "Amelia",
        display_short: b.display_short || "Amelia",
        display_tagline: b.display_tagline || "Suite de Gestión Empresarial",
        primary_color: b.primary_color || "",
        logo_url: b.logo_url || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const applyPreset = async (name) => {
    if (!window.confirm(`¿Aplicar preset "${PRESET_META[name]?.label}"? Esto sobreescribe la configuración actual de módulos.`)) return;
    try {
      await api.post(`/settings/modules/preset/${name}`);
      toast.success(`Preset "${PRESET_META[name]?.label}" aplicado`);
      await load(); await refreshBrand();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = (id) => {
    setState((s) => ({ ...s, enabled: { ...s.enabled, [id]: !s.enabled[id] } }));
  };

  // Toggle masivo por categoría: enciende/apaga todos los no-core de la categoría
  const toggleCategory = (catItems, nextState) => {
    setState((s) => {
      const nxt = { ...s.enabled };
      catItems.forEach((m) => {
        if (!m.core) nxt[m.id] = nextState;
      });
      return { ...s, enabled: nxt };
    });
  };

  const [query, setQuery] = useState("");

  const saveModules = async () => {
    setSaving(true);
    try {
      await api.put("/settings/modules", { enabled: state.enabled, industry: "custom" });
      toast.success("Módulos guardados. Refresca el menú lateral para ver cambios.");
      await load(); await refreshBrand();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      await api.put("/settings/branding", brand);
      toast.success("Branding actualizado");
      await refreshBrand();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingBrand(false); }
  };

  if (!state) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const enabledCount = Object.values(state.enabled).filter(Boolean).length;
  const totalCount = state.catalog.length;

  return (
    <div className="space-y-8" data-testid="modules-tab">
      {/* Branding */}
      <section className="border border-border rounded-md p-4 bg-card">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="w-4 h-4 text-primary" />
          <h3 className="font-bold">Branding de la Suite</h3>
          <Badge variant="outline" className="text-[10px] ml-auto">RESELLER / WHITE-LABEL</Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Personaliza el nombre del software si lo revendes bajo otra marca. El nombre del cliente final (ej. "Ferretería El Bodegón") se configura en el tab <b>Empresa</b>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="overline">Nombre del producto</Label>
            <Input value={brand.product_name} onChange={(e) => setBrand({ ...brand, product_name: e.target.value })} placeholder="Amelia" className="mt-1" data-testid="brand-product-name" />
          </div>
          <div>
            <Label className="overline">Nombre corto</Label>
            <Input value={brand.display_short} onChange={(e) => setBrand({ ...brand, display_short: e.target.value })} placeholder="Amelia" className="mt-1" data-testid="brand-short" />
          </div>
          <div className="md:col-span-2">
            <Label className="overline">Tagline</Label>
            <Input value={brand.display_tagline} onChange={(e) => setBrand({ ...brand, display_tagline: e.target.value })} placeholder="Suite de Gestión Empresarial" className="mt-1" />
          </div>
        </div>
        <Button onClick={saveBrand} disabled={savingBrand} className="mt-4 bg-primary font-bold" data-testid="save-brand-btn">
          {savingBrand ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Guardar branding
        </Button>
      </section>

      {/* Presets */}
      <section className="border border-border rounded-md p-4 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <Package2 className="w-4 h-4 text-primary" />
          <h3 className="font-bold">Presets por industria</h3>
          <Badge className="text-[10px] ml-auto">
            Actual: {state.industry}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Aplica un preset base y luego ajusta módulos individuales. Los <b>módulos core</b> no se pueden desactivar.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {state.presets.map((name) => {
            const meta = PRESET_META[name] || { icon: Package2, label: name };
            const Icon = meta.icon;
            const isActive = state.industry === name;
            return (
              <button
                key={name}
                onClick={() => applyPreset(name)}
                className={`border rounded-md p-3 hover:border-primary transition-colors text-left ${isActive ? "border-primary bg-primary/5" : "border-border"}`}
                data-testid={`preset-${name}-btn`}
              >
                <Icon className={`w-5 h-5 ${meta.color || "text-primary"}`} />
                <div className="font-bold text-sm mt-1.5">{meta.label}</div>
                <div className="text-[10px] text-muted-foreground capitalize mt-0.5">{isActive && "✓ activo"}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Módulos individuales */}
      <section className="border border-border rounded-md p-4 bg-card">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Package2 className="w-4 h-4 text-primary" />
          <h3 className="font-bold">Módulos individuales</h3>
          <Badge className="text-[10px]">
            Activos: {enabledCount}/{totalCount}
          </Badge>
          <div className="flex items-center gap-2 ml-auto flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar módulo…"
              className="h-8"
              data-testid="modules-search"
            />
            {query && (
              <Button size="sm" variant="ghost" onClick={() => setQuery("")} className="h-7 px-2">
                <X className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          <Button onClick={saveModules} disabled={saving} size="sm" className="bg-primary font-bold" data-testid="save-modules-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Guardar
          </Button>
        </div>

        <div className="space-y-4">
          {Object.entries(state.categories).map(([cat, items]) => {
            const q = query.trim().toLowerCase();
            const filtered = q
              ? items.filter((m) =>
                  m.label.toLowerCase().includes(q) ||
                  m.desc?.toLowerCase().includes(q) ||
                  m.id.toLowerCase().includes(q),
                )
              : items;
            if (!filtered.length) return null;
            const nonCore = items.filter((m) => !m.core);
            const activeNonCore = nonCore.filter((m) => state.enabled[m.id]).length;
            const allOn = nonCore.length > 0 && activeNonCore === nonCore.length;
            const allOff = activeNonCore === 0;
            return (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="overline text-muted-foreground">{CATEGORY_LABELS[cat] || cat}</div>
                  <Badge variant="outline" className="text-[10px]">
                    {activeNonCore}/{nonCore.length}
                  </Badge>
                  {nonCore.length > 0 && cat !== "core" && (
                    <div className="ml-auto flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        disabled={allOn}
                        onClick={() => toggleCategory(items, true)}
                        data-testid={`cat-${cat}-all-on`}
                        title="Activar toda la categoría"
                      >
                        <ToggleRight className="w-3.5 h-3.5 mr-1" />
                        Activar todo
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        disabled={allOff}
                        onClick={() => toggleCategory(items, false)}
                        data-testid={`cat-${cat}-all-off`}
                        title="Desactivar toda la categoría"
                      >
                        <ToggleLeft className="w-3.5 h-3.5 mr-1" />
                        Apagar todo
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {filtered.map((m) => {
                    const isCore = m.core;
                    const checked = isCore || state.enabled[m.id];
                    return (
                      <label
                        key={m.id}
                        className={`flex items-start gap-3 p-3 rounded-md border transition-colors ${
                          checked ? "border-primary/40 bg-primary/5" : "border-border"
                        } ${isCore ? "opacity-80" : "cursor-pointer hover:border-primary/60"}`}
                        data-testid={`module-row-${m.id}`}
                      >
                        <Switch
                          checked={checked}
                          onCheckedChange={() => !isCore && toggle(m.id)}
                          disabled={isCore}
                          data-testid={`module-switch-${m.id}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-sm flex items-center gap-1.5">
                            {m.label}
                            {isCore && <Badge variant="outline" className="text-[9px]">CORE</Badge>}
                            {m.deps && m.deps.length > 0 && (
                              <Badge variant="outline" className="text-[9px]" title={`Requiere: ${m.deps.join(", ")}`}>
                                deps: {m.deps.join(", ")}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">{m.desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
